import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer.js';
import Graphic from '@arcgis/core/Graphic.js';
import Point from '@arcgis/core/geometry/Point.js';
import type Geometry from '@arcgis/core/geometry/Geometry.js';
import {
  MEASURE_FILL,
  MEASURE_FILL_HIGHLIGHT,
  MEASURE_LINE,
  MEASURE_LINE_HIGHLIGHT,
  measureLabelSymbol,
} from '@/map/symbols';
import {
  formatArea,
  formatDistance,
  nextMeasurementId,
  useMeasurementsStore,
  type MeasurementEntry,
  type MeasurementMode,
} from '@/state/measurementsStore';
import { getMapView } from './mapStore';

export const MEASUREMENTS_LAYER_ID = '__rcmc_measurements';

let layer: GraphicsLayer | null = null;

export function ensureMeasurementsLayer(): GraphicsLayer {
  if (layer) return layer;
  layer = new GraphicsLayer({
    id: MEASUREMENTS_LAYER_ID,
    title: 'Measurements',
    listMode: 'hide',
  });
  return layer;
}

export function getMeasurementsLayer(): GraphicsLayer | null {
  return layer;
}

function labelPoint(geometry: Geometry): Point | null {
  const extent = (geometry as { extent?: __esri.Extent }).extent;
  if (!extent) return null;
  return new Point({
    x: (extent.xmin + extent.xmax) / 2,
    y: (extent.ymin + extent.ymax) / 2,
    spatialReference: extent.spatialReference,
  });
}

export function commitMeasurement(args: {
  mode: MeasurementMode;
  geometry: Geometry;
  valueMeters: number;
  perimeterMeters?: number;
}): MeasurementEntry {
  const target = ensureMeasurementsLayer();
  const id = nextMeasurementId();
  const label =
    args.mode === 'distance'
      ? formatDistance(args.valueMeters)
      : formatArea(args.valueMeters);

  const geomGraphic = new Graphic({
    geometry: args.geometry,
    symbol: args.mode === 'distance' ? MEASURE_LINE : MEASURE_FILL,
    attributes: { __measurement_id: id, __role: 'geometry' },
  });

  const lp = labelPoint(args.geometry);
  const labelGraphic = lp
    ? new Graphic({
        geometry: lp,
        symbol: measureLabelSymbol(label),
        attributes: { __measurement_id: id, __role: 'label' },
      })
    : null;

  const graphics = labelGraphic ? [geomGraphic, labelGraphic] : [geomGraphic];
  target.addMany(graphics);

  const entry: MeasurementEntry = {
    id,
    mode: args.mode,
    valueMeters: args.valueMeters,
    perimeterMeters: args.perimeterMeters,
    label,
    geometry: args.geometry,
    graphics,
  };
  useMeasurementsStore.getState().add(entry);
  return entry;
}

export function removeMeasurement(id: string): void {
  const target = layer;
  const entry = useMeasurementsStore.getState().entries.find((e) => e.id === id);
  if (entry && target) target.removeMany(entry.graphics);
  useMeasurementsStore.getState().remove(id);
}

export function clearMeasurements(): void {
  const target = layer;
  if (target) target.removeAll();
  useMeasurementsStore.getState().clear();
}

export function flashMeasurement(id: string): void {
  const view = getMapView();
  const entry = useMeasurementsStore.getState().entries.find((e) => e.id === id);
  if (!entry) return;

  const geomGraphic = entry.graphics[0];
  if (!geomGraphic) return;

  const baseSymbol = entry.mode === 'distance' ? MEASURE_LINE : MEASURE_FILL;
  const highlightSymbol =
    entry.mode === 'distance' ? MEASURE_LINE_HIGHLIGHT : MEASURE_FILL_HIGHLIGHT;

  geomGraphic.symbol = highlightSymbol;
  window.setTimeout(() => {
    geomGraphic.symbol = baseSymbol;
  }, 900);

  if (view) {
    const extent = (entry.geometry as { extent?: __esri.Extent }).extent;
    if (extent) {
      void view.goTo(extent.expand(1.4)).catch(() => undefined);
    }
  }
}

export type { MeasurementEntry, MeasurementMode } from '@/state/measurementsStore';
